import { SamplesProPlan } from 'constants/brands';
import React, { useRef } from 'react';
import { Interval } from '@landr/pricing/lib/src/types/generated';
import { Trans } from '@lingui/macro';
import { DarkMode, InfoOutlined, Popover, useBreakpoint } from '@landr/maestro';
import { StudioPlanCodes } from '@landr/pricing';
import Plan from '../Plan/Plan';
import { StyledLi } from '../../styled';
import { useUpsellActionsContext } from '../../context/UpsellActionsContext';
import useStudioPlan from './useStudioPlan';

export type StudioPlanProps = {
    planCode: string;
};

const StudioEssentialUsp = ({ frequency }: { frequency: Interval }) => {
    const popOverAnchorRef = useRef<HTMLDivElement>(null);
    const isMobile = useBreakpoint('md');

    return (
        <>
            <Trans>
                Everything in <strong>{SamplesProPlan}</strong> plus:
            </Trans>
            <ul>
                <li className={StyledLi} key="unlimited-mp3-mastering">
                    <Trans>Unlimited AI mastering (MP3)</Trans>
                </li>
                <li className={StyledLi} key="unlimited-distribution">
                    <Trans>Unlimited music distribution</Trans>
                </li>
                <li className={StyledLi} key="landr-sampler-and-plugins">
                    <Trans>
                        LANDR Sampler + 10 additional LANDR{' '}
                        <span style={{ whiteSpace: 'nowrap' }}>
                            plugins
                            <span
                                ref={popOverAnchorRef}
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                <DarkMode>
                                    <Popover
                                        hasArrow
                                        placement="top"
                                        triggerEvent={
                                            isMobile ? 'click' : 'hover'
                                        }
                                        anchorRef={popOverAnchorRef}
                                    >
                                        <Popover.Trigger>
                                            <InfoOutlined
                                                color="icon.low"
                                                size="sm"
                                            />
                                        </Popover.Trigger>
                                        <Popover.Content>
                                            <Trans>
                                                <ul>
                                                    <li>
                                                        Boost your creativity
                                                        with these essential
                                                        plugins:
                                                    </li>
                                                    <li>- LANDR Guitar</li>
                                                    <li>- LANDR FX Suite</li>
                                                    <li>- LANDR Synth X</li>
                                                    <li>- LANDR Sampler</li>
                                                    <li>- LANDR Chromatic</li>
                                                    <li>- RePitch Elements</li>
                                                    <li>
                                                        - VocAlign 6 Standard
                                                    </li>
                                                </ul>
                                            </Trans>
                                        </Popover.Content>
                                    </Popover>
                                </DarkMode>
                            </span>
                        </span>
                    </Trans>
                </li>
                <li className={StyledLi} key="unlimited-distribution-premium">
                    <Trans>Unlimited distribution with premium features</Trans>
                </li>
                <li className={StyledLi} key="1000-worth-of-extra">
                    <Trans>
                        <span
                            style={{
                                textDecoration:
                                    frequency === 'Monthly'
                                        ? 'line-through'
                                        : 'none',
                            }}
                        >
                            $1000 worth of essential tools, plugins, and{' '}
                            <span style={{ whiteSpace: 'nowrap' }}>
                                courses
                                <span
                                    ref={popOverAnchorRef}
                                    onClick={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    <DarkMode>
                                        <Popover
                                            hasArrow
                                            placement="top"
                                            triggerEvent={
                                                isMobile ? 'click' : 'hover'
                                            }
                                            anchorRef={popOverAnchorRef}
                                        >
                                            <Popover.Trigger>
                                                <InfoOutlined
                                                    color="icon.low"
                                                    size="sm"
                                                />
                                            </Popover.Trigger>
                                            <Popover.Content>
                                                <Trans>
                                                    <ul>
                                                        <li>
                                                            All Studio
                                                            subscribers get:
                                                        </li>
                                                        <li>
                                                            - Baby Audio TAIP
                                                        </li>
                                                        <li>
                                                            - Ableton Live 12
                                                            Lite
                                                        </li>
                                                        <li>
                                                            - Access to 10+
                                                            additional curated
                                                            music production
                                                            tools including
                                                            must-have VST and
                                                            effects plugins from
                                                            partners like IK
                                                            Multimedia, Arturia
                                                            & Spitfire Audio
                                                        </li>
                                                        <li>
                                                            - Unlimited access
                                                            to 200+ in-depth,
                                                            interactive music
                                                            creation, production
                                                            and promotion
                                                            courses
                                                        </li>
                                                        <li>
                                                            - Video
                                                            collaboration
                                                            sessions let you
                                                            share HD audio from
                                                            your DAW in
                                                            real-time
                                                        </li>
                                                    </ul>
                                                </Trans>
                                            </Popover.Content>
                                        </Popover>
                                    </DarkMode>
                                </span>
                            </span>
                        </span>{' '}
                    </Trans>
                </li>
            </ul>
        </>
    );
};
const StudioStandardUsp = ({ frequency }: { frequency: Interval }) => (
    <>
        <Trans>
            Everything in <strong>{SamplesProPlan}</strong> plus:
        </Trans>
        <ul>
            {frequency === 'Monthly' ? (
                <li className={StyledLi} key="50-credits">
                    <Trans>50 additionnal credits per month</Trans>
                </li>
            ) : (
                <li className={StyledLi} key="600-credits">
                    <Trans>600 additionnal credits per year</Trans>
                </li>
            )}
            {frequency === 'Monthly' ? (
                <li className={StyledLi} key="unlimited-mp3-3-wav-masters">
                    <Trans>Unlimited MP3 + 3 WAV masters per month</Trans>
                </li>
            ) : (
                <li className={StyledLi} key="unlimited-mp3-36-wav-masters">
                    <Trans>Unlimited MP3 + 36 WAV masters per year</Trans>
                </li>
            )}
            <li className={StyledLi} key="unlimited-distribution">
                <Trans>Unlimited distribution with premium features</Trans>
            </li>
            {frequency === 'Monthly' ? (
                <li className={StyledLi} key="1000-worth-of-extra">
                    <Trans>
                        <span
                            style={{
                                textDecoration: 'line-through',
                            }}
                        >
                            $3,000
                        </span>{' '}
                        <span>
                            $1,000 worth of other must-have tools, plugins and
                            courses
                        </span>
                    </Trans>
                </li>
            ) : (
                <li className={StyledLi} key="3000-worth-of-extra">
                    <Trans>
                        $3,000 worth of other must-have tools, plugins and
                        courses
                    </Trans>
                </li>
            )}
        </ul>
    </>
);

const StudioPlan = ({ planCode }: StudioPlanProps) => {
    const {
        frequency,
        hasStudioPlan,
        hasSamplesPlan,
        selectedPlan,
        studioCatalog,
        handlePlanSelect,
        getDiscountedPlan,
    } = useUpsellActionsContext();

    const {
        code,
        currency,
        info,
        monthlyEquivalentPrice,
        monthlyEquivalentSale,
        salePrice,
        subscriptionPrice,
        title,
    } = useStudioPlan({
        frequency,
        hasSamplesPlan,
        studioCatalog,
        getDiscountedPlan,
        planCode,
    });

    if (hasStudioPlan) {
        return null;
    }

    const isVerticalLayout = useBreakpoint('lg');

    return (
        <Plan
            code={code}
            currency={currency}
            frequency={frequency}
            handlePlanSelect={handlePlanSelect}
            info={info}
            isChecked={selectedPlan === code}
            isMostPopular={true}
            isVerticalLayout={isVerticalLayout}
            monthlyEquivalentPrice={monthlyEquivalentPrice}
            monthlyEquivalentSale={monthlyEquivalentSale}
            salePrice={salePrice}
            subscriptionPrice={subscriptionPrice}
            title={title}
            usp={
                planCode === StudioPlanCodes.StudioEssentials ? (
                    <StudioEssentialUsp frequency={frequency} />
                ) : (
                    <StudioStandardUsp frequency={frequency} />
                )
            }
        />
    );
};

export default StudioPlan;
